import {CalendarOutlined, CheckCircleOutlined, DeleteOutlined, DollarOutlined, EditOutlined, EnvironmentOutlined, PhoneOutlined, UserOutlined, WalletOutlined} from '@ant-design/icons';
import {useQueryClient} from '@tanstack/react-query';
import {DatePicker, Divider, Drawer, Popconfirm, Spin, Tag, Tooltip} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {FaCheck} from 'react-icons/fa';
import {IoShieldCheckmarkOutline} from 'react-icons/io5';
import {RxCross2} from 'react-icons/rx';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../../CommonComponents/Cards';
import {TableData} from '../../../../CommonComponents/TableData';
import {RqDeleteUser, RqSupervisorLookupAll, RqSupervisorUpdate} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';
import {EditEmployeeScreen} from '../EditEmployee';

const ROLE_COLORS = {
  EMPLOYEE:   'blue',
  SUPERVISOR: 'purple',
  ACCOUNTANT: 'cyan',
  ADMIN:      'volcano',
};

const formatRupee = val =>
  val != null ? `₹ ${Number(val).toLocaleString('en-IN')}` : '—';

const StatusTag = ({status}) => (
  <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>{status}</Tag>
);
StatusTag.propTypes = {status: PropTypes.string};

const EmployeeDrawer = ({employee, open, onClose}) => {
  const [selectedMonth, setSelectedMonth] = useState(null);

  const salary = useMemo(() => {
    if (!employee || !selectedMonth || !employee.salaryInfo?.length) {
      return null;
    }

    const m = selectedMonth.month() + 1;
    const y = selectedMonth.year();

    return (
      employee.salaryInfo.find(
        s => s.month === m && s.year === y,
      ) ?? null
    );
  }, [selectedMonth, employee]);

  const attendance = useMemo(() => {
    if (!employee) {return [];}

    if (!employee.attendanceInfo?.length) {return [];}

    if (!selectedMonth) {return employee.attendanceInfo;}

    const start = selectedMonth.startOf('month');
    const end = selectedMonth.endOf('month');

    return employee.attendanceInfo.filter(a => {
      const d = dayjs(a.date);

      return (
        (d.isAfter(start) || d.isSame(start, 'day')) &&
        (d.isBefore(end) || d.isSame(end, 'day'))
      );
    });
  }, [selectedMonth, employee]);

  const attendanceSummary = useMemo(() => {
    const present = attendance.filter(a => a.status === 'PRESENT').length;
    const absent = attendance.filter(a => a.status === 'ABSENT').length;
    const halfDay = attendance.filter(a => a.status === 'HALF_DAY').length;
    const overtime = attendance.reduce(
      (sum, a) => sum + (a.overtimeHours ?? 0),
      0,
    );

    return {
      present,
      absent,
      halfDay,
      overtime,
      total: attendance.length,
    };
  }, [attendance]);

  if (!employee) {
    return null;
  }

  const attendanceCols = [
    {
      title: 'Date', dataIndex: 'date', key: 'date', width: 110,
      render: v => dayjs(v).format('DD MMM YYYY'),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: v => {
        const colorMap = {PRESENT: 'green', ABSENT: 'red', HALF_DAY: 'orange'};
        return <Tag color={colorMap[v] ?? 'default'}>{v}</Tag>;
      },
    },
    {
      title: 'Overtime', dataIndex: 'overtimeType', key: 'overtimeType', width: 80,
      render: v => v ? <span className={'text-primary-600 text-sm font-medium'}>{v}</span> : '—',
    },
    {
      title: 'Login Time', dataIndex: 'loginTime', key: 'loginTime', width: 110,
      render: v => v ? dayjs(v).format('hh:mm A') : '—',
    },
    {title: 'Marked By', dataIndex: 'markedByRole', key: 'markedByRole', width: 110},
    {
      title: 'Reason', dataIndex: 'reason', key: 'reason', width: 140,
      render: v => v || <span className={'text-gray-400'}>—</span>,
    },
  ];

  return (
    <Drawer
      title={(
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold'}
            style={{background: 'linear-gradient(135deg, #3a5f98, #a7b8d1)'}}
          >
            {employee.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <p className={'font-bold text-gray-800 m-0 leading-tight'}>{employee.name}</p>
            <p className={'text-xs text-gray-400 m-0'}>{employee.email}</p>
          </div>
        </div>
      )}
      open={open}
      onClose={onClose}
      width={720}
      styles={{body: {padding: '16px 24px'}}}
    >
      {/* Basic Info */}
      <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5'}>
        <div className={'flex items-center gap-3'}>
          <PhoneOutlined className={'text-blue-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Mobile</p>
            <p className={'font-medium m-0'}>{employee.mobile}</p>
          </div>
        </div>

        <div className={'flex items-center gap-3'}>
          <EnvironmentOutlined className={'text-red-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Address</p>
            <p className={'font-medium m-0'}>{employee.address || '—'}</p>
          </div>
        </div>

        <div className={'flex items-center gap-3'}>
          <UserOutlined className={'text-purple-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Role</p>
            <Tag color={ROLE_COLORS[employee.roles] ?? 'default'}>
              {employee.roles}
            </Tag>
          </div>
        </div>

        <div className={'flex items-center gap-3'}>
          <CheckCircleOutlined className={'text-green-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Status</p>
            <StatusTag status={employee.status} />
          </div>
        </div>

        <div className={'flex items-center gap-3'}>
          <DollarOutlined className={'text-green-600 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Per Day</p>
            <p className={'font-semibold text-green-600 m-0'}>
              {formatRupee(employee.perDaysWages)}
            </p>
          </div>
        </div>

        <div className={'flex items-center gap-3'}>
          <WalletOutlined className={'text-blue-600 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Wages Rate</p>
            <p className={'font-semibold text-blue-600 m-0'}>
              {formatRupee(employee.wagesRate)}
            </p>
          </div>
        </div>
      </div>
      <Divider className={'bg-primary-50'} />

      {/* Month Filter */}
      <div className={'flex items-center gap-3 mb-4 pt-3 flex-wrap'}>
        <CalendarOutlined className={'text-primary-800'} />
        <span className={'text-sm font-semibold text-gray-500 uppercase tracking-wide'}>
          Filter by Month:
        </span>
        <DatePicker
          picker={'month'}
          value={selectedMonth}
          onChange={val => setSelectedMonth(val)}
          allowClear
          placeholder={'Select month'}
          style={{width: 160}}
          disabledDate={current => current && current > dayjs().endOf('month')}
        />
        {selectedMonth && (
          <span className={'text-xs text-primary-900 font-medium bg-primary-50 px-3 py-1 rounded-md border border-primary-200'}>
            {selectedMonth.format('MMMM YYYY')}
          </span>
        )}
      </div>

      {/* Salary Card */}
      <div className={'mb-5'}>
        <p className={'text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3'}>
          Salary Details {selectedMonth ? `— ${selectedMonth.format('MMMM YYYY')}` : '(select a month)'}
        </p>
        {salary ? (
          <div className={'grid grid-cols-2 md:grid-cols-4 gap-3'}>
            {[
              {label: 'Total Days',   value: salary.totalDays,        color: 'text-gray-700'},
              {label: 'Present',      value: salary.presentDays,      color: 'text-green-700'},
              {label: 'Absent',       value: salary.absentDays,       color: 'text-red-600'},
              {label: 'Half Days',    value: salary.halfDays,         color: 'text-orange-500'},
              {label: 'OT Amount',     value: formatRupee(salary.overtimeAmount), color: 'text-purple-600'},
              {label: 'Gross Salary', value: formatRupee(salary.grossSalary),     color: 'text-green-700'},
              {label: 'Advance Ded.', value: formatRupee(salary.advanceDeducted), color: 'text-red-600'},
              {label: 'Final Salary', value: formatRupee(salary.finalSalary),     color: 'text-primary-700'},
            ].map(item => (
              <div key={item.label} className={'bg-primary-50 border border-primary-200 rounded-xl px-3 py-3 text-center shadow-sm'}>
                <p className={'text-xs text-gray-400 m-0 mb-1'}>{item.label}</p>
                <p className={`font-bold text-base m-0 ${item.color}`}>{item.value}</p>
              </div>
            ))}
            {salary.distributedDate && (
              <div className={'col-span-2 md:col-span-4 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2 text-sm'}>
                <span className={'text-gray-500'}>Distributed On: </span>
                <span className={'font-semibold text-green-700'}>{salary.distributedDate}</span>
                <span className={'mx-3 text-gray-300'}>|</span>
                <span className={'text-gray-500'}>Period: </span>
                <span className={'font-medium text-gray-700'}>{salary.salaryFrom} → {salary.salaryTo}</span>
              </div>
            )}
          </div>
        ) : (
          <div className={'bg-gray-50 border border-dashed border-gray-200 rounded-xl py-6 text-center text-gray-400 text-sm'}>
            {selectedMonth ? 'No salary record found for this month' : 'Select a month to view salary details'}
          </div>
        )}
      </div>

      {/* Attendance Table */}
      <div>
        <div className={'flex items-center justify-between mb-3'}>
          <p className={'text-xs font-semibold text-gray-400 uppercase tracking-widest m-0'}>
            Attendance {selectedMonth ? `— ${selectedMonth.format('MMMM YYYY')}` : '(All)'}
          </p>
          {/* Attendance summary chips */}
          <div className={'flex gap-3 pt-5 flex-wrap'}>
            <Tag className={'text-sm'} color={'green'}>{attendanceSummary.present} Present</Tag>
            <Tag className={'text-sm'}  color={'red'}>{attendanceSummary.absent} Absent</Tag>
            <Tag className={'text-sm'}  color={'orange'}>{attendanceSummary.halfDay} Half</Tag>
            <Tag  className={'text-sm'} color={'purple'}>{attendanceSummary.overtime}h OT</Tag>
          </div>
        </div>
        <TableData
          columns={attendanceCols}
          dataSource={attendance.map((a, i) => ({...a, key: a.id ?? i}))}
          size={'small'}
          scroll={{x: 'max-content'}}
          locale={{emptyText: 'No attendance records'}}
        />
      </div>
    </Drawer>
  );
};

EmployeeDrawer.propTypes = {
  employee: PropTypes.object,
  open:     PropTypes.bool,
  onClose:  PropTypes.func,
};

const EmployeeTab = () => {
  const auth  = useSelector(selectAuth);
  const token = auth?.tokenData;

  const [drawerState, setDrawerState] = useState({open: false, employee: null});

  const {data, isLoading} = RqSupervisorLookupAll(token);

  const [editModal, setEditModal] = useState({
    open: false,
    employee: null,
  });

  const queryClient = useQueryClient();

  const employees = useMemo(() => {
    if (!data?.length) {return [];}
    return data
      .filter(u => u.roles === 'EMPLOYEE')
      .map((u, i) => ({
        key:           u.id,
        id:            u.id,
        srno:          i + 1,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        firstName: u.firstName,
        lastName: u.lastName,

        email: u.email,
        mobile: u.mobile,

        address: u.address,
        street: u.street,
        apartment: u.apartment,
        pinCode: u.pinCode,
        city: u.city,
        district: u.district,
        state: u.state,

        roles: u.roles,
        status: u.status,
        verifcation: u.verifcation,

        perDaysWages: u.perDaysWages ?? 0,
        wagesRate: u.wagesRate ?? 0,

        businessName: u.businessName,
        gstIn: u.gstIn,

        salaryInfo: u.salaryInfo ?? [],
        attendanceInfo: u.attendanceInfo ?? [],
      }));
  }, [data]);

  const stats = useMemo(() => {
    const active   = employees.filter(u => u.status      === 'ACTIVE').length;
    const inactive = employees.filter(u => u.status      === 'INACTIVE').length;
    const verified = employees.filter(u => u.verifcation === true).length;
    return {total: employees.length, active, inactive, verified};
  }, [employees]);

  const {mutate: updateEmployee, isLoading: isUpdating} =
  RqSupervisorUpdate({
    onSuccess: () => {
      toast.success('Employee updated');

      queryClient.invalidateQueries({
        queryKey: ['rq_supervisor_lookup_all'],
      });
    },
    onError: err =>
      toast.error(err?.message || 'Update failed'),
  });

  const {mutate: deleteEmployee} =
  RqDeleteUser({
    onSuccess: () => {
      toast.success('Employee deleted');

      queryClient.invalidateQueries({
        queryKey: ['rq_supervisor_lookup_all'],
      });
    },
    onError: err =>
      toast.error(err?.message || 'Delete failed'),
  });

  const columns = [
    {title: 'Sr.',  dataIndex: 'srno',  key: 'srno',  width: 55},
    {title: 'Name', dataIndex: 'name',  key: 'name',  width: 160,
      render: v => <span className={'font-medium text-gray-800'}>{v}</span>},
    {
      title: 'Email', dataIndex: 'email', key: 'email', width: 200, ellipsis: true,
      render: v => <Tooltip title={v}><span className={'text-gray-600'}>{v}</span></Tooltip>,
    },
    {title: 'Mobile', dataIndex: 'mobile', key: 'mobile', width: 120},
    
    {
      title: 'Per Day (₹)', dataIndex: 'perDaysWages', key: 'perDaysWages', width: 120,
      render: val => <span className={'font-medium text-green-700'}>{formatRupee(val)}</span>,
    },
    {
      title: 'Wages Rate (₹)', dataIndex: 'wagesRate', key: 'wagesRate', width: 130,
      render: val => <span className={'font-medium text-blue-700'}>{formatRupee(val)}</span>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 95,
      render: status => <StatusTag status={status} />,
    },

    {
      title: 'Action',
      key: 'action',
      width: 110,
      render: (_, record) => (
        <button
          className={'flex gap-2'}
          onClick={e => e.stopPropagation()}
        >
          <Tooltip title={'Edit'}>
            <EditOutlined
              className={'cursor-pointer border rounded-md p-1 text-blue-600'}
              onClick={() =>
                setEditModal({
                  open: true,
                  employee: record,
                })
              }
            />
          </Tooltip>

          <Tooltip title={'Delete'}>
            <Popconfirm
              title={'Delete Employee'}
              description={'Are you sure?'}
              okText={'Delete'}
              cancelText={'Cancel'}
              okButtonProps={{danger: true}}
              onConfirm={() =>
                deleteEmployee({
                  token,
                  id: record.id,
                })
              }
            >
              <DeleteOutlined
                className={'cursor-pointer border rounded-md p-1 text-red-600'}
              />
            </Popconfirm>
          </Tooltip>
        </button>
      ),
    },
   
  ];

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-64'}>
        <Spin size={'large'} />
      </div>
    );
  }

  return (
    <div className={'md:p-4 p-1'}>
      {/* Stats */}
      <div className={'grid grid-cols-2 md:grid-cols-4 gap-3 mb-5'}>
        <StatCard label={'Total'}    value={stats.total}    icon={<UserOutlined className={'bg-color'} />}                     bg={'#e6e9ec'} />
        <StatCard label={'Active'}   value={stats.active}   icon={<FaCheck className={'bg-color'} />}                          bg={'#e6e9ec'} />
        <StatCard label={'Inactive'} value={stats.inactive} icon={<RxCross2 className={'bg-color'} />}                         bg={'#e6e9ec'} />
        <StatCard label={'Verified'} value={stats.verified} icon={<IoShieldCheckmarkOutline className={'text-lg bg-color'} />} bg={'#e6e9ec'} />
      </div>

      <p className={'text-xs text-gray-400 mb-3'}>
        Click any row to view salary &amp; attendance details
      </p>

      {/* Table */}
      <div className={'overflow-x-auto rounded-lg '}>
        <TableData
          columns={columns}
          dataSource={employees}
          rowKey={'id'}
          size={'middle'}
          scroll={{x: 'max-content'}}
          rowClassName={'hover:bg-indigo-50 transition-colors cursor-pointer'}
          locale={{emptyText: 'No employees found'}}
          onRow={record => ({
            onClick: () => setDrawerState({open: true, employee: record}),
          })}
        />
      </div>

      {/* Detail Drawer */}
      <EmployeeDrawer
        open={drawerState.open}
        employee={drawerState.employee}
        onClose={() => setDrawerState({open: false, employee: null})}
      />
      <EditEmployeeScreen
        open={editModal.open}
        employee={editModal.employee}
        token={token}
        loggedInRole={auth?.user?.role}
        onSave={updateEmployee}
        isUpdating={isUpdating}
        onClose={() =>
          setEditModal({
            open: false,
            employee: null,
          })
        }
      />
    </div>
  );
};

export {EmployeeTab};
export default EmployeeTab;
